import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import dashboardService from '../services/dashboardService';
import {
  Users,
  BarChart3,
  Calendar,
  Settings,
  Shield,
  ShieldCheck,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Calculator,
  Package,
  Warehouse,
  PlayCircle,
  FileText,
  DollarSign,
  GraduationCap,
  AlertCircle,
  Bell,
  Clock3,
  PackageCheck,
  Activity
} from 'lucide-react';
import AppointmentStats from './AppointmentStats';
import { useCostStats } from '../hooks/useCosts';

const isManager = (role) => role === 'GERENCIA' || role === 'ADMIN' || role === 'CEO';

const Dashboard = () => {
  const { user, hasRole } = useAuth();

  // Separação de acesso entre membros da GERENCIA:
  // Identificação EXCLUSIVA do gerente Iago
  // Nunca deve atingir Aline (ID 7 / gerencia@santiago.com) nem outros gerentes
  const isIagoUser = useMemo(() => {
    const email = (user?.email || '').toLowerCase().trim();
    const fullName = (user?.full_name || '').toLowerCase().trim();
    const userId = Number(user?.id);

    // Se for Aline, garante que NUNCA seja considerado Iago
    if (userId === 7 || email.includes('gerencia@santiago.com') || fullName.includes('aline')) {
      return false;
    }

    return (
      userId === 13 ||
      email === 'iago201910@gmail.com' ||
      email.startsWith('iago') ||
      fullName.includes('iago henrique')
    );
  }, [user]);

  // Regra de visualização de métricas financeiras/custos:
  // - Aline: acesso total a todos os indicadores (custos, valor total, projetos, funcionários)
  // - Iago: não visualiza os cards de 'Total de Custos' e 'Valor Total'
  const canViewCostMetrics = useMemo(() => {
    if (!isManager(user?.role)) return false;

    // Suporte também a permissão negada no banco de dados caso seja configurado
    const isDenied =
      user?.deniedPermissions?.includes('costs:view_summary') ||
      user?.deniedPermissions?.includes('costs:view') ||
      user?.financial_access === false ||
      user?.deniedPermissions?.includes('financial:view');

    if (isIagoUser || isDenied) {
      return false;
    }

    return true;
  }, [user, isIagoUser]);

  // Se o usuário não tem permissão de ver custos, nem dispara a requisição de estatísticas
  const { data: costStats, isLoading: loadingCostStats } = useCostStats({}, { enabled: canViewCostMetrics });

  const { data: taskCountsData, isLoading: loadingTaskCounts } = useQuery({
    queryKey: ['dashboard-task-counts'],
    queryFn: () => dashboardService.getTaskCounts(),
    enabled: hasRole(['PRODUCAO', 'RECEPCAO_COMPRAS_AGENDA', 'GERENCIA', 'ADMIN', 'CEO'])
  });

  const { data: userData, isLoading: loadingUserData } = useQuery({
    queryKey: ['user-last-activity', user?.id],
    queryFn: () => api.get(`/users/${user.id}`),
    enabled: !!user?.id
  });

  const { data: employeesData, isLoading: loadingEmployees } = useQuery({
    queryKey: ['employees-count'],
    queryFn: () => api.get('/users', { params: { active: 'true', limit: 1 } }),
    enabled: isManager(user?.role)
  });

  const { data: producaoSummary, isLoading: loadingProducaoSummary } = useQuery({
    queryKey: ['producao-dashboard-summary'],
    queryFn: () => api.get('/dashboard/producao-summary'),
    enabled: hasRole(['PRODUCAO', 'MONTAGEM_CLIENTE', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA'])
  });

  // BUG-01 fix: backend retorna { data: { user: { last_login } } }
  const lastLogin = userData?.data?.data?.user?.last_login;

  const formatLastActivity = (timestamp, isLoading) => {
    if (isLoading) return 'Carregando...';
    if (!timestamp) return 'Sem registro';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `Há ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return date.toLocaleDateString('pt-BR');
  };

  const taskCounts = taskCountsData?.data || {};
  // BUG-03 fix: backend retorna { pagination: { total } }
  const employeesCount = employeesData?.data?.data?.pagination?.total || 0;

  const getRoleGreeting = () => {
    const greetings = {
      GERENCIA: 'Bem-vindo ao painel executivo',
      ADMIN: 'Bem-vindo ao painel executivo',
      CEO: 'Bem-vindo ao painel executivo',
      RECEPCAO_COMPRAS_AGENDA: 'Bem-vindo ao painel de compras e agenda',
      RECEPCAO_PROJETOS: 'Bem-vindo ao painel de projetos',
      PRODUCAO: 'Bem-vindo ao painel de produção',
      MONTAGEM_CLIENTE: 'Bem-vindo ao painel de montagem'
    };
    return greetings[user?.role] || 'Bem-vindo ao sistema';
  };

  const lastActivityLabel = formatLastActivity(lastLogin, loadingUserData);

  const widgets = useMemo(() => {
    const baseWidgets = [
      {
        title: 'Última Atividade',
        value: lastActivityLabel,
        icon: Clock,
        color: 'bg-[#49943d]',
        description: 'Último acesso ao sistema'
      }
    ];

    if (isManager(user?.role)) {
      baseWidgets.unshift(
        {
          title: 'Total de Funcionários',
          value: loadingEmployees ? '...' : employeesCount.toString(),
          icon: Users,
          color: 'bg-primary-500',
          description: 'Funcionários ativos no sistema'
        },
        {
          title: 'Levantamentos no Mês',
          value: loadingTaskCounts ? '...' : (taskCounts.surveys || 0).toString(),
          icon: BarChart3,
          color: 'bg-green-500',
          description: 'Levantamentos iniciados este mês'
        }
      );
    }

    if (hasRole(['PRODUCAO'])) {
      baseWidgets.unshift(
        {
          title: 'Tarefas Pendentes',
          value: loadingTaskCounts ? '...' : (taskCounts.demands || 0).toString(),
          icon: AlertTriangle,
          color: 'bg-warning-500',
          description: 'Tarefas aguardando execução'
        },
        {
          title: 'Tarefas Concluídas',
          value: '0',
          icon: CheckCircle,
          color: 'bg-success-500',
          description: 'Tarefas finalizadas hoje'
        }
      );
    }

    if (hasRole(['RECEPCAO_COMPRAS_AGENDA', 'GERENCIA', 'ADMIN', 'CEO'])) {
      baseWidgets.unshift(
        {
          title: 'Agendamentos',
          value: loadingTaskCounts ? '...' : (taskCounts.appointments || 0).toString(),
          icon: Calendar,
          color: 'bg-purple-500',
          description: 'Agendamentos pendentes'
        },
        {
          title: 'Projetos',
          value: loadingTaskCounts ? '...' : (taskCounts.projects || 0).toString(),
          icon: TrendingUp,
          color: 'bg-[#49943d]',
          description: 'Projetos em andamento'
        }
      );

      if (canViewCostMetrics && costStats?.data?.stats && !loadingCostStats) {
        const stats = costStats.data.stats;
        baseWidgets.unshift(
          {
            title: 'Total de Custos',
            value: stats.total_costs?.toString() || '0',
            icon: Calculator,
            color: 'bg-[#74af41]',
            description: 'Itens cadastrados no sistema'
          },
          {
            title: 'Valor Total',
            value: new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(stats.total_cost_value || 0),
            icon: Package,
            color: 'bg-[#74af41]',
            description: 'Valor total dos custos'
          }
        );
      }
    }

    return baseWidgets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, canViewCostMetrics, loadingEmployees, employeesCount, loadingTaskCounts, taskCounts, costStats, loadingCostStats, lastActivityLabel]);

  const quickActions = useMemo(() => {
    const actions = [];

    if (!hasRole(['PRODUCAO', 'MONTAGEM_CLIENTE'])) {
      actions.push({
        title: 'Novo Agendamento',
        description: 'Criar um novo agendamento',
        href: '/agendamentos/novo',
        icon: Calendar,
        color: 'bg-[#49943d] hover:bg-[#3a7630]'
      });
    }

    if (isIagoUser) {
      actions.push(
        {
          title: 'Gerenciar Funcionários',
          description: 'Adicionar, editar ou remover funcionários',
          href: '/users',
          icon: Users,
          color: 'bg-primary-600 hover:bg-primary-700'
        },
        {
          title: 'Sábados Trabalhados',
          description: 'Controlar sábados trabalhados da equipe',
          href: '/users/sabados-trabalhados',
          icon: Calendar,
          color: 'bg-blue-600 hover:bg-blue-700'
        },
        {
          title: 'Horas Extras',
          description: 'Lançar e gerenciar horas extras',
          href: '/users/horas-extras',
          icon: Clock,
          color: 'bg-indigo-600 hover:bg-indigo-700'
        },
        {
          title: 'Gestão de Custos',
          description: 'Cadastrar e gerenciar custos da empresa',
          href: '/custos',
          icon: Calculator,
          color: 'bg-orange-600 hover:bg-orange-700'
        },
        {
          title: 'Gestão de Armazéns',
          description: 'Gerenciar locais de estoque',
          href: '/armazens',
          icon: Warehouse,
          color: 'bg-teal-600 hover:bg-teal-700'
        },
        {
          title: 'Jornadas & Configurações',
          description: 'Configurar jornadas de trabalho e horários',
          href: '/config/jornadas',
          icon: Settings,
          color: 'bg-secondary-600 hover:bg-secondary-700'
        },
        {
          title: 'Gerenciar Permissões',
          description: 'Controlar acesso de cada role às funcionalidades',
          href: '/config/permissoes',
          icon: ShieldCheck,
          color: 'bg-purple-600 hover:bg-purple-700'
        }
      );
    } else if (isManager(user?.role)) {
      actions.push(
        {
          title: 'Gerenciar Funcionários',
          description: 'Adicionar, editar ou remover funcionários',
          href: '/users',
          icon: Users,
          color: 'bg-primary-600 hover:bg-primary-700'
        },
        {
          title: 'Gestão de Custos',
          description: 'Cadastrar e gerenciar custos da empresa',
          href: '/custos',
          icon: Calculator,
          color: 'bg-orange-600 hover:bg-orange-700'
        },
        {
          title: 'Gestão de Armazéns',
          description: 'Gerenciar locais de estoque',
          href: '/armazens',
          icon: Warehouse,
          color: 'bg-teal-600 hover:bg-teal-700'
        },
        {
          title: 'Jornadas & Configurações',
          description: 'Configurar jornadas de trabalho e horários',
          href: '/config/jornadas',
          icon: Settings,
          color: 'bg-secondary-600 hover:bg-secondary-700'
        },
        {
          title: 'Gerenciar Permissões',
          description: 'Controlar acesso de cada role às funcionalidades',
          href: '/config/permissoes',
          icon: ShieldCheck,
          color: 'bg-purple-600 hover:bg-purple-700'
        }
      );
    }

    if (hasRole(['RECEPCAO_COMPRAS_AGENDA'])) {
      actions.push(
        {
          title: 'Gestão de Custos',
          description: 'Cadastrar e gerenciar custos da empresa',
          href: '/custos',
          icon: Calculator,
          color: 'bg-orange-600 hover:bg-orange-700'
        },
        {
          title: 'Gestão de Armazéns',
          description: 'Gerenciar locais de estoque',
          href: '/armazens',
          icon: Warehouse,
          color: 'bg-teal-600 hover:bg-teal-700'
        }
      );
    }

    if (hasRole(['PRODUCAO', 'MONTAGEM_CLIENTE', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA'])) {
      actions.push(
        {
          title: 'Meus Atendimentos',
          description: 'Ver meus atendimentos',
          href: '/atendimentos',
          icon: PlayCircle,
          color: 'bg-[#49943d] hover:bg-[#3a7630]'
        },
        {
          title: 'Meus Holerites',
          description: 'Ver meus holerites',
          href: '/meus-holerites',
          icon: FileText,
          color: 'bg-[#74af41] hover:bg-[#5d8f34]'
        },
        {
          title: 'Meus Registros',
          description: 'Horas extras e sábados',
          href: '/meus-registros',
          icon: Clock,
          color: 'bg-[#49943d] hover:bg-[#3a7630]'
        },
        {
          title: 'Adiantamento Quinzenal',
          description: 'Solicitar adiantamento',
          href: '/adiantamento',
          icon: DollarSign,
          color: 'bg-[#49943d] hover:bg-[#3a7630]'
        },
        {
          title: 'Treinamentos',
          description: 'Acessar treinamentos',
          href: '/treinamentos',
          icon: GraduationCap,
          color: 'bg-[#74af41] hover:bg-[#5d8f34]'
        }
      );
    }

    actions.push({
      title: 'Meu Perfil',
      description: 'Atualizar informações pessoais',
      href: '/profile',
      icon: Shield,
      color: 'bg-[#49943d] hover:bg-[#3a7630]'
    });

    return actions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const getRoleBadgeColor = (role) => {
    const colors = {
      GERENCIA: 'bg-red-100 text-red-800',
      ADMIN: 'bg-red-100 text-red-800',
      CEO: 'bg-red-100 text-red-800',
      RECEPCAO_COMPRAS_AGENDA: 'bg-[#d9ead9] text-[#2c5824]',
      RECEPCAO_PROJETOS: 'bg-[#d4f0d9] text-[#4a6f29]',
      PRODUCAO: 'bg-yellow-100 text-yellow-800',
      MONTAGEM_CLIENTE: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-secondary-100 text-secondary-800';
  };

  // BUG-09 fix: buscar atividades recentes reais do sistema apenas para GERENCIA / ADMIN / CEO
  const { data: recentActivities, isLoading: loadingActivities } = useQuery({
    queryKey: ['dashboard-recent-activities'],
    queryFn: () => api.get('/dashboard/tasks', { params: { limit: 5 } }),
    enabled: !!user?.id && isManager(user?.role),
    staleTime: 60000
  });

  const formatActivityDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffHours < 1) return 'Agora';
    if (diffHours < 24) return `Há ${diffHours}h`;
    if (diffDays < 7) return `Há ${diffDays}d`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">
              Olá, {user?.full_name}!
            </h1>
            <p className="text-sm sm:text-base text-secondary-600 mt-1">
              {getRoleGreeting()}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getRoleBadgeColor(user?.role)}`}>
              {user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Widgets de estatísticas - ocultos para Iago e setores operacionais */}
      {!isIagoUser && !hasRole(['PRODUCAO', 'MONTAGEM_CLIENTE', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA']) && widgets.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {widgets.map((widget, index) => (
            <div key={`${widget.title}-${index}`} className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex items-center">
                <div className={`p-2 sm:p-3 rounded-md ${widget.color} flex-shrink-0`}>
                  <widget.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-secondary-600 truncate">
                    {widget.title}
                  </p>
                  <p className="text-xl sm:text-2xl font-semibold text-secondary-900">
                    {widget.value}
                  </p>
                </div>
              </div>
              <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-secondary-500">
                {widget.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Estatísticas de Agendamentos - ocultas para Iago e setores operacionais */}
      {!isIagoUser && !hasRole(['PRODUCAO', 'MONTAGEM_CLIENTE', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA']) && <AppointmentStats />}

      {/* Resumo Minhas Tarefas - apenas para setores operacionais */}
      {hasRole(['PRODUCAO', 'MONTAGEM_CLIENTE', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA']) && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gradient-to-r from-[#49943d] to-[#5d8f34] px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <FileText className="h-5 w-5 sm:h-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-white">
                    Minhas Tarefas
                  </h2>
                  <p className="text-xs sm:text-sm text-white/80">
                    Resumo do seu quadro de demandas
                  </p>
                </div>
              </div>
              <Link
                to="/demands"
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white text-[#49943d] text-xs sm:text-sm font-medium rounded-lg hover:bg-white/90 transition-colors"
              >
                Ver Kanban
              </Link>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {loadingProducaoSummary ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#49943d]"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                  <div className="bg-red-50 rounded-lg p-3 sm:p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-red-100 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      </div>
                      <span className="text-xs font-medium text-red-600">Atrasado</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-red-700">
                      {producaoSummary?.data?.demands?.overdue || 0}
                    </p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4 border border-orange-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-orange-100 rounded-lg">
                        <Bell className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="text-xs font-medium text-orange-600">A Vencer</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-orange-700">
                      {producaoSummary?.data?.demands?.dueSoon || 0}
                    </p>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-3 sm:p-4 border border-yellow-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-yellow-100 rounded-lg">
                        <Clock3 className="h-4 w-4 text-yellow-600" />
                      </div>
                      <span className="text-xs font-medium text-yellow-600">Em Tratativa</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-yellow-700">
                      {producaoSummary?.data?.demands?.emTratativa || 0}
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-green-100 rounded-lg">
                        <PackageCheck className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-xs font-medium text-green-600">Resolvido</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-green-700">
                      {producaoSummary?.data?.demands?.resolvido || 0}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#49943d]"></div>
                    <span className="text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">{producaoSummary?.data?.demands?.total || 0}</span> demandas
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                    <span className="text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">{producaoSummary?.data?.appointments || 0}</span> agendamentos
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                    <span className="text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">{producaoSummary?.data?.demands?.pending || 0}</span> pendentes
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Ações rápidas */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-secondary-900 mb-3 sm:mb-4">
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={`${action.title}-${index}`}
              to={action.href}
              className={`block p-3 sm:p-4 rounded-lg text-white transition-colors ${action.color}`}
            >
              <div className="flex items-center">
                <action.icon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                <div className="ml-2 sm:ml-3 min-w-0">
                  <p className="font-medium text-sm sm:text-base truncate">{action.title}</p>
                  <p className="text-xs sm:text-sm opacity-90 line-clamp-1">{action.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Atividades recentes - visível apenas para GERENCIA / ADMIN / CEO (exceto Iago) */}
      {isManager(user?.role) && !isIagoUser && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-secondary-900 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Atividades Recentes
            </h2>
            {recentActivities?.data?.tasks?.length > 0 && (
              <Link to="/demands" className="text-sm text-[#49943d] hover:text-[#3a7630] font-medium">
                Ver todas →
              </Link>
            )}
          </div>
          {loadingActivities ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#49943d]"></div>
            </div>
          ) : recentActivities?.data?.tasks?.length > 0 ? (
            <div className="space-y-2">
              {recentActivities.data.tasks.slice(0, 5).map((task) => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      task.status === 'overdue' ? 'bg-red-500' :
                      task.status === 'due_soon' ? 'bg-orange-500' :
                      task.status === 'in_progress' ? 'bg-blue-500' :
                      task.status === 'done' ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                      <p className="text-xs text-gray-500">{task.source_label}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{formatActivityDate(task.due_date)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-secondary-500 text-center py-4">
              Nenhuma atividade recente para exibir
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
